import React, { useState, useEffect, useRef } from "react"
import { QRCodeCanvas } from "qrcode.react";
import { adminApiFetch, normalizeVillas } from "./api";

function Guests() {
    const baseUrl = window._env_?.REACT_APP_API_URL || process.env.REACT_APP_BASE_URL;
    const [guests, setGuests] = useState([]);
    const [editedGuests, setEditedGuests] = useState([]);
    const [newGuests, setNewGuests] = useState([]);
    const [villas, setVillas] = useState([]);
    const [errorMsg, setErrorMsg] = useState(""); // Error message
    const [deletedGuestIds, setDeletedGuestIds] = useState([]);
    const [isSaving, setIsSaving] = useState(false);

    // Modal state for guest link
    const [showModal, setShowModal] = useState(false);
    const [modalLink, setModalLink] = useState("");
    const [modalGuestId, setModalGuestId] = useState(null);
    const [linkCopyStatus, setLinkCopyStatus] = useState("");
    const [qrCopyStatus, setQrCopyStatus] = useState("");
    const qrWrapperRef = useRef(null);

    const isAnyRequiredFieldEmpty = () => {
        const requiredFields = ["first_name", "last_name", "arrival_date", "departure_date", "villa_id"];
        for (const guest of editedGuests) {
            // Check only non-deleted guests
            if (!deletedGuestIds.includes(guest.id)) {
                for (const field of requiredFields) {
                    if (!guest[field]) return true;
                }
            }
        }
        for (const guest of newGuests) {
            for (const field of requiredFields) {
                if (!guest[field]) return true;
            }
        }
        return false;
    };

    useEffect(() => {
        adminApiFetch(`${baseUrl}/guests`)
            .then(res => res.json())
            .then(data => {
                setGuests(data);
                setEditedGuests(data.map(g => ({ ...g })));
            })
            .catch(() => setGuests([]));
        adminApiFetch(`${baseUrl}/villas`)
            .then(res => res.json())
            .then(data => setVillas(normalizeVillas(data)))
            .catch(() => setVillas([]));
    }, [baseUrl]);

    const handleChange = (idx, field, value) => {
        const updated = [...editedGuests];
        updated[idx][field] = value;
        setEditedGuests(updated);
    };

    const handleNewGuestChange = (idx, field, value) => {
        const updated = [...newGuests];
        updated[idx][field] = value;
        setNewGuests(updated);
    };

    const handleAddNewGuestRow = () => {
        setNewGuests([
            ...newGuests,
            {
                first_name: "", last_name: "", arrival_date: "",
                departure_date: "", villa_id: "", internal_notes: ""
            }
        ]);
    };

    const isGuestChanged = (original, edited) => {
        const fields = [
            "first_name", "last_name", "arrival_date",
            "departure_date", "villa_id", "internal_notes"
        ];
        return fields.some(field => (original[field] || "") !== (edited[field] || ""));
    };

    const handleSave = async () => {
        if (isSaving) return;
        setErrorMsg("");

        const processPayload = (guest) => ({
            ...guest,
            internal_notes: guest.internal_notes || null,
        });

        const changedGuests = editedGuests.filter((edited, idx) => {
            const original = guests[idx];
            return isGuestChanged(original, edited);
        });
        const changedGuestsForSave = changedGuests.filter(g => !deletedGuestIds.includes(g.id));
        const guestsToDelete = guests.filter(g => deletedGuestIds.includes(g.id));

        const handleResponse = async (res, defaultError) => {
            if (res.ok) return res.status === 204 ? null : res.json();
            let err;
            try {
                err = await res.json();
            } catch {
                throw new Error(defaultError);
            }
            throw new Error(err.detail?.[0]?.msg || err.detail || defaultError);
        };

        try {
            setIsSaving(true);

            if (guestsToDelete.length > 0) {
                const deleteRes = await adminApiFetch(`${baseUrl}/guests`, {
                    method: "DELETE",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(guestsToDelete)
                });
                await handleResponse(deleteRes, "Error while deleting");
            }

            if (changedGuestsForSave.length > 0) {
                const updateRes = await adminApiFetch(`${baseUrl}/guests`, {
                    method: "PUT",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(changedGuestsForSave.map(processPayload))
                });
                await handleResponse(updateRes, "Error while updating");
            }

            if (newGuests.length > 0) {
                const createRes = await adminApiFetch(`${baseUrl}/guests`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(newGuests.map(processPayload))
                });
                await handleResponse(createRes, "Error while creating");
            }

            const reload = await adminApiFetch(`${baseUrl}/guests`);
            const data = await reload.json();
            setGuests(data);
            setEditedGuests(data.map(g => ({ ...g })));
            setNewGuests([]);
            setDeletedGuestIds([]);
        } catch (err) {
            setErrorMsg(err.message);
        } finally {
            setIsSaving(false);
        }
    };

    // Delete button for new guest row or existing guest
    const handleRemoveNewGuestRow = (idx) => {
        setNewGuests(newGuests.filter((_, i) => i !== idx));
    };

    const handleMarkGuestForDelete = (id) => {
        if (deletedGuestIds.includes(id)) {
            setDeletedGuestIds(deletedGuestIds.filter(delId => delId !== id));
        } else {
            setDeletedGuestIds([...deletedGuestIds, id]);
        }
    };

    // Open guest link modal
    const handleEditClick = async (guestId) => {
        try {
            const res = await adminApiFetch(`${baseUrl}/guest/${guestId}/link`);
            const data = await res.json();
            setModalLink(typeof data === "string" && data ? data : "No link found");
            setModalGuestId(guestId);
            setShowModal(true);
        } catch {
            setModalLink("Error loading link");
            setModalGuestId(guestId);
            setShowModal(true);
        }
    };

    const changedCount = editedGuests.reduce((count, guest, idx) => {
        const original = guests[idx];
        if (!original || deletedGuestIds.includes(guest.id)) return count;
        return isGuestChanged(original, guest) ? count + 1 : count;
    }, 0);

    const pendingCount = changedCount + newGuests.length + deletedGuestIds.length;
    const modalGuest = editedGuests.find(g => g.id === modalGuestId);
    const modalGuestName = `${modalGuest?.first_name || ""} ${modalGuest?.last_name || ""}`.trim();
    const hasValidModalLink = modalLink && modalLink.startsWith("http");

    const closeModal = () => {
        setShowModal(false);
        setLinkCopyStatus("");
        setQrCopyStatus("");
    };

    const handleCopyLink = async () => {
        if (!modalLink || typeof modalLink !== "string") return;
        try {
            await navigator.clipboard.writeText(modalLink);
            setLinkCopyStatus("Link copied");
        } catch {
            setLinkCopyStatus("Copy failed");
        }
    };

    const handleCopyQrCode = async () => {
        const canvas = qrWrapperRef.current?.querySelector("canvas");
        if (!canvas) {
            setQrCopyStatus("QR not available");
            return;
        }

        try {
            if (typeof ClipboardItem !== "undefined") {
                canvas.toBlob(async (blob) => {
                    if (!blob) {
                        setQrCopyStatus("Copy failed");
                        return;
                    }
                    const item = new ClipboardItem({ "image/png": blob });
                    await navigator.clipboard.write([item]);
                    setQrCopyStatus("QR copied");
                });
                return;
            }
            setQrCopyStatus("Clipboard image API unavailable");
        } catch {
            setQrCopyStatus("Copy failed");
        }
    };

    useEffect(() => {
        if (!showModal) return;

        const onEsc = (event) => {
            if (event.key === "Escape") {
                closeModal();
            }
        };

        window.addEventListener("keydown", onEsc);
        return () => window.removeEventListener("keydown", onEsc);
    }, [showModal]);

    return (
        <div className="management-page">
            <div className="management-page-header">
                <h2 className="management-page-title">Guest Dashboard</h2>
                <div className="management-badge-row">
                    <span className="management-badge management-badge-neutral">Total: {editedGuests.length}</span>
                    <span className="management-badge management-badge-new">New: {newGuests.length}</span>
                    <span className="management-badge management-badge-changed">Changed: {changedCount}</span>
                    <span className="management-badge management-badge-delete">Delete: {deletedGuestIds.length}</span>
                    <span className="management-badge management-badge-neutral">Pending: {pendingCount}</span>
                </div>
            </div>

            <div className="management-callout">
                Required: first name, last name, arrival, departure and villa. Changes are applied after clicking "Save all Changes".
            </div>

            {errorMsg && (
                <div className="management-error">{errorMsg}</div>
            )}

            <div className="management-table-shell">
            <table className="management-table">
                <thead>
                <tr>
                    <th>ID</th>
                    <th>First Name*</th>
                    <th>Last Name*</th>
                    <th>Arrival*</th>
                    <th>Departure*</th>
                    <th>Villa*</th>
                    <th>Internal Notes</th>
                    <th></th>
                </tr>
                </thead>
                <tbody>
                {editedGuests.map((guest, idx) => {
                    const isDeleted = deletedGuestIds.includes(guest.id);
                    const isChanged = !isDeleted && isGuestChanged(guests[idx], guest);
                    const rowClass = isDeleted
                        ? "management-table-row-delete"
                        : (isChanged ? "management-table-row-changed" : "");

                    return (
                    <tr key={guest.id} className={rowClass}>
                        <td>
                            <div className="management-row-id">
                                <span>{guest.id}</span>
                                {isChanged && <span className="management-badge management-badge-changed">changed</span>}
                                {isDeleted && <span className="management-badge management-badge-delete">delete</span>}
                            </div>
                        </td>
                        <td>
                            <input name="first_name" value={guest.first_name || ""} onChange={e => handleChange(idx, "first_name", e.target.value)} disabled={isDeleted} />
                        </td>
                        <td>
                            <input name="last_name" value={guest.last_name || ""} onChange={e => handleChange(idx, "last_name", e.target.value)} disabled={isDeleted} />
                        </td>
                        <td>
                            <input
                                name="arrival_date"
                                type="date"
                                value={guest.arrival_date ? guest.arrival_date.slice(0, 10) : ""}
                                onChange={e => handleChange(idx, "arrival_date", e.target.value)}
                                disabled={isDeleted}
                            />
                        </td>
                        <td>
                            <input
                                name="departure_date"
                                type="date"
                                value={guest.departure_date ? guest.departure_date.slice(0, 10) : ""}
                                onChange={e => handleChange(idx, "departure_date", e.target.value)}
                                disabled={isDeleted}
                            />
                        </td>
                        <td>
                            <select
                                value={guest.villa_id || ""}
                                onChange={e => handleChange(idx, "villa_id", e.target.value)}
                                disabled={isDeleted}
                            >
                                <option value="">Choose Villa</option>
                                {villas.map(villa => (
                                    <option key={villa.id} value={villa.id}>{villa.name}</option>
                                ))}
                            </select>
                        </td>
                        <td>
                            <input name="internal_notes" value={guest.internal_notes || ""} onChange={e => handleChange(idx, "internal_notes", e.target.value)} disabled={isDeleted} />
                        </td>
                        <td>
                            <button
                                type="button"
                                onClick={() => handleEditClick(guest.id)}
                                className="management-icon-btn"
                                aria-label="Copy guest link"
                                title="Guest link and QR code"
                                disabled={isDeleted}
                            >
                                <svg width="20" height="20" viewBox="0 0 64 64" fill="none">
                                    <rect x="18" y="18" width="28" height="36" rx="4" stroke="black" strokeWidth="4" fill="white"/>
                                    <rect x="10" y="10" width="28" height="36" rx="4" stroke="black" strokeWidth="4" fill="white"/>
                                </svg>
                            </button>
                            <button
                                type="button"
                                onClick={() => handleMarkGuestForDelete(guest.id)}
                                className="management-action-btn"
                                aria-label="Delete guest / row"
                                title={isDeleted ? "Undo delete guest / row" : "Delete guest / row"}
                            >
                                {isDeleted ? "↺" : "✖"}
                            </button>
                        </td>
                    </tr>
                );})}
                {newGuests.map((guest, idx) => (
                    <tr key={`new-${idx}`} className="management-table-row-new">
                        <td>
                            <div className="management-row-id">
                                <span>new</span>
                                <span className="management-badge management-badge-new">new</span>
                            </div>
                        </td>
                        <td>
                            <input name="first_name" value={guest.first_name} onChange={e => handleNewGuestChange(idx, "first_name", e.target.value)} />
                        </td>
                        <td>
                            <input name="last_name" value={guest.last_name} onChange={e => handleNewGuestChange(idx, "last_name", e.target.value)} />
                        </td>
                        <td>
                            <input name="arrival_date" type="date" value={guest.arrival_date} onChange={e => handleNewGuestChange(idx, "arrival_date", e.target.value)} />
                        </td>
                        <td>
                            <input name="departure_date" type="date" value={guest.departure_date} onChange={e => handleNewGuestChange(idx, "departure_date", e.target.value)} />
                        </td>
                        <td>
                            <select
                                value={guest.villa_id || ""}
                                onChange={e => handleNewGuestChange(idx, "villa_id", e.target.value)}
                            >
                                <option value="">Choose Villa</option>
                                {villas.map(villa => (
                                    <option key={villa.id} value={villa.id}>{villa.name}</option>
                                ))}
                            </select>
                        </td>
                        <td>
                            <input name="internal_notes" value={guest.internal_notes} onChange={e => handleNewGuestChange(idx, "internal_notes", e.target.value)} />
                        </td>
                        <td>
                            <button
                                type="button"
                                onClick={() => handleRemoveNewGuestRow(idx)}
                                className="management-action-btn"
                                aria-label="Delete guest / row"
                                title="Delete guest / row"
                            >
                                &#10006;
                            </button>
                        </td>
                    </tr>
                ))}
                </tbody>
            </table>
            </div>

            <div className="management-action-bar">
            <button
                onClick={handleAddNewGuestRow}
                disabled={isAnyRequiredFieldEmpty() || isSaving}
            >
                Create New Guest
            </button>
            <button onClick={handleSave}
                    disabled={isAnyRequiredFieldEmpty() || isSaving}>
                {isSaving ? "Saving..." : "Save all Changes"}
            </button>
            </div>

            {showModal && (
                <div className="management-modal-backdrop">
                    <div className="management-modal">
                        <div className="management-modal-header">
                            <h3 className="management-modal-title">Link for guest {modalGuestName || "Unknown"}</h3>
                            <button
                                type="button"
                                onClick={closeModal}
                                className="management-modal-close-btn"
                                aria-label="Close guest link modal"
                                title="Close"
                            >
                                x
                            </button>
                        </div>

                        <p className="management-modal-subtitle">
                            Share the direct guest login URL or use the QR code for quick mobile access.
                        </p>

                        <div className="management-modal-content-grid">
                            <section className="management-modal-card">
                                <label className="management-modal-label" htmlFor="guest-link-field">Guest link</label>
                                <div className="management-modal-link-row">
                                    <input
                                        id="guest-link-field"
                                        readOnly
                                        value={modalLink || "No link found"}
                                        className="management-modal-link-input"
                                    />
                                </div>

                                <div className="management-modal-actions">
                                    <button
                                        type="button"
                                        onClick={handleCopyLink}
                                        className="management-modal-primary-btn"
                                        disabled={!modalLink}
                                    >
                                        Copy link
                                    </button>
                                    <a
                                        href={hasValidModalLink ? modalLink : "#"}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={`management-modal-secondary-btn ${!hasValidModalLink ? "is-disabled" : ""}`}
                                        onClick={(event) => {
                                            if (!hasValidModalLink) event.preventDefault();
                                        }}
                                    >
                                        Open link
                                    </a>
                                </div>

                                <p className="management-modal-feedback" aria-live="polite">{linkCopyStatus}</p>
                            </section>

                            <section className="management-modal-card management-modal-qr-card">
                                <div ref={qrWrapperRef} className="management-modal-qr-wrapper">
                                    {hasValidModalLink ? (
                                        <QRCodeCanvas value={modalLink} size={168} />
                                    ) : (
                                        <p className="management-modal-feedback">QR code unavailable</p>
                                    )}
                                </div>

                                <button
                                    type="button"
                                    onClick={handleCopyQrCode}
                                    className="management-modal-secondary-btn"
                                    disabled={!hasValidModalLink}
                                >
                                    Copy QR code
                                </button>
                                <p className="management-modal-feedback" aria-live="polite">{qrCopyStatus}</p>
                            </section>
                        </div>

                        <div className="management-modal-footer">
                            <button type="button" onClick={closeModal} className="management-modal-secondary-btn">Close</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Guests;